import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, User, Sparkles, Phone, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { betaApply, betaMe, betaState, conciergeChat, conciergeEvent, conciergeHandoff } from "@/lib/api";
import { track } from "@/lib/analytics";

const SESSION_KEY = "ashlan_concierge_sid";
const STORE_KEY = "ashlan_concierge_msgs";
const NUDGE_KEY = "ashlan_concierge_nudged";
const SUPPRESS_KEY = "ashlan_concierge_suppress_autopop";  // set when user says "don't show again"
const SEEN_KEY = "ashlan_concierge_seen";  // first-time vs returning detection
const AUTOPOP_DELAY_MS = 6000;  // 6-second auto-pop default
const AUTOPOP_RETURNING_DELAY_MS = 4000;
const PAGELOAD_AT = Date.now();
const WELCOME = {
  role: "assistant",
  content:
    "Hi — I'm the Ashlan Clinic concierge. Ask me anything about our therapists, services, Therabody products or how to book. I'm here to help but I can't diagnose. If you need a real person, tap **Talk to a real person** below the chat.",
};

function loadSession() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = (window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)).replace(/-/g, "");
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    const raw = sessionStorage.getItem(STORE_KEY);
    const msgs = raw ? JSON.parse(raw) : [WELCOME];
    return { sid, msgs };
  } catch {
    return { sid: Math.random().toString(36).slice(2), msgs: [WELCOME] };
  }
}

function saveMsgs(msgs) {
  try { sessionStorage.setItem(STORE_KEY, JSON.stringify(msgs)); } catch (e) { /* noop */ }
}

export default function ConciergeChat() {
  const [open, setOpen] = useState(false);
  const [{ sid, msgs }, setState] = useState(() => loadSession());
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [visitor, setVisitor] = useState({
    role: "visitor",
    name: null,
    tier: null,
    phase: "unknown",
    capacity_left: null,
  });
  const scrollRef = useRef(null);
  const scrolledRef = useRef(false);

  // On mount, prefetch beta context + tester record so the LLM (and the UI)
  // know whether they're talking to an unlocked tester or a curious visitor.
  useEffect(() => {
    Promise.all([betaMe().catch(() => null), betaState().catch(() => null)]).then(([m, s]) => {
      const next = { role: "visitor", name: null, tier: null, phase: s?.phase || "unknown", capacity_left: null };
      if (m?.unlocked) {
        next.role = "tester";
        next.name = m.name || null;
        next.tier = m.tier || null;
      }
      if (s) {
        try {
          const cap = s.max_testers || 30;
          // Hint capacity from `state` if backend ever exposes it; for now we
          // leave null and rely on the per-chat backend injection.
          next.capacity_left = cap;
        } catch { /* noop */ }
      }
      setVisitor(next);
      // Inject a one-time persona-aware welcome when relevant.
      if (next.role === "tester") {
        setState((prev) => {
          if (prev.msgs.length === 1 && prev.msgs[0]?.role === "assistant") {
            return {
              ...prev,
              msgs: [
                {
                  role: "assistant",
                  content: `Welcome back, **${(next.name || "tester").split(" ")[0]}** — thanks for being part of the Early Access Preview. Ask me anything about the site, or tap **Send feedback** at the bottom-right when you have a moment.`,
                },
              ],
            };
          }
          return prev;
        });
      }
    });
  }, []);

  // Auto-pop: open the panel after a short delay for every visitor unless
  // they have explicitly opted out ("Don't show again") or already engaged
  // this session. Respects returning visitors with a slightly faster opener.
  useEffect(() => {
    let cancelled = false;
    const suppressed = (() => {
      try { return localStorage.getItem(SUPPRESS_KEY) === "1"; } catch { return false; }
    })();
    const alreadyOpenedThisSession = (() => {
      try { return sessionStorage.getItem("ashlan_concierge_autopopped") === "1"; } catch { return false; }
    })();
    const isReturning = (() => {
      try {
        const seen = localStorage.getItem(SEEN_KEY);
        if (!seen) {
          localStorage.setItem(SEEN_KEY, "1");
          return false;
        }
        return true;
      } catch { return false; }
    })();
    if (suppressed || alreadyOpenedThisSession || open) return;
    const delay = isReturning ? AUTOPOP_RETURNING_DELAY_MS : AUTOPOP_DELAY_MS;
    const timer = setTimeout(() => {
      if (cancelled || open) return;
      try { sessionStorage.setItem("ashlan_concierge_autopopped", "1"); } catch (e) { /* noop */ }
      setOpen(true);
      try {
        conciergeEvent({
          session_id: sid,
          event: "autopop_shown",
          page_path: typeof window !== "undefined" ? window.location.pathname : null,
          dwell_ms: Date.now() - PAGELOAD_AT,
          meta: { returning: isReturning },
        });
      } catch (e) { /* noop */ }
      track("concierge_autopop_shown", { returning: isReturning ? 1 : 0 });
      if (isReturning) {
        conciergeEvent({
          session_id: sid,
          event: "returning_visitor",
          page_path: typeof window !== "undefined" ? window.location.pathname : null,
        });
      }
    }, delay);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [sid, open]);

  // Proactive nudge — fire once per session after 45 s + scroll, but only
  // if Concierge hasn't been opened and the visitor is not already a tester
  // (testers don't need to be pitched the beta they're already in).
  useEffect(() => {
    const dismissed = sessionStorage.getItem(NUDGE_KEY) === "1";
    if (dismissed) return;
    const onScroll = () => {
      scrolledRef.current = true;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    const timer = setTimeout(() => {
      const dwell = Date.now() - PAGELOAD_AT;
      if (
        !open &&
        scrolledRef.current &&
        dwell >= 45_000 &&
        sessionStorage.getItem(NUDGE_KEY) !== "1" &&
        visitor.role !== "tester" &&
        visitor.phase !== "ended"
      ) {
        setShowNudge(true);
        sessionStorage.setItem(NUDGE_KEY, "1");
        track("concierge_nudge_shown");
      }
    }, 46_000);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [open, visitor.role, visitor.phase]);

  // Scroll to bottom on new message / open.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, msgs.length, busy]);

  useEffect(() => { saveMsgs(msgs); }, [msgs]);

  const setMsgs = (updater) =>
    setState((prev) => ({ ...prev, msgs: typeof updater === "function" ? updater(prev.msgs) : updater }));

  const openChat = () => {
    const wasAutoPopped = (() => {
      try { return sessionStorage.getItem("ashlan_concierge_autopopped") === "1"; } catch { return false; }
    })();
    setOpen(true);
    track("concierge_open");
    conciergeEvent({
      session_id: sid,
      event: "panel_opened",
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      dwell_ms: Date.now() - PAGELOAD_AT,
      meta: { triggered_by: wasAutoPopped ? "autopop" : "user_click" },
    });
  };

  const closeChat = () => {
    const wasAutoPopped = (() => {
      try { return sessionStorage.getItem("ashlan_concierge_autopopped") === "1"; } catch { return false; }
    })();
    setOpen(false);
    conciergeEvent({
      session_id: sid,
      event: "panel_closed",
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      dwell_ms: Date.now() - PAGELOAD_AT,
      meta: { from_autopop: wasAutoPopped, messages: msgs.length },
    });
  };

  const suppressAutopop = () => {
    try { localStorage.setItem(SUPPRESS_KEY, "1"); } catch (e) { /* noop */ }
    toast.success("Got it — we won't auto-open the chat again on this device.");
    closeChat();
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((cur) => [...cur, { role: "user", content: text }]);
    try {
      const res = await conciergeChat({
        session_id: sid,
        message: text,
        // NB: `visitor_role/name/tier` were previously sent from the client
        // and are now derived server-side from the beta access cookie.
        page_path: typeof window !== "undefined" ? window.location.pathname : null,
      });
      let reply = res.reply || "";
      // Backend strips [APPLY|…] from the visible reply and surfaces the
      // parsed fields as `apply_intent`. We lodge it via /beta/applications.
      const intent = res.apply_intent;
      if (intent && intent.name && intent.email && intent.reason) {
        const elapsedSec = Math.max(8, Math.floor((Date.now() - PAGELOAD_AT) / 1000));
        try {
          const apply = await betaApply({
            name: intent.name,
            email: intent.email,
            tier: intent.tier || "site_test",
            reason: intent.reason,
            source: "concierge",
            hp: "",
            elapsed_seconds: elapsedSec,
          });
          track("beta_apply_submit", { status: apply.status, tier: intent.tier });
          const followup =
            apply.status === "invited"
              ? `✔ Application sent. Your invite code is **${apply.code}** — check your inbox (we also emailed it to ${intent.email}).`
              : apply.status === "waitlist"
                ? "✔ Application received — we've hit the 30-tester cap so you're on the waitlist. We'll be in touch if a spot opens."
                : apply.status === "already_invited"
                  ? "You're already on our list — check your inbox for the invite."
                  : "Got it — we've logged your application.";
          reply = reply ? `${reply}\n\n${followup}` : followup;
        } catch (err) {
          const detail = err?.response?.data?.detail;
          reply =
            (reply ? reply + "\n\n" : "") +
            "Hmm — I couldn't lodge that application: " +
            (typeof detail === "string"
              ? detail
              : "please try again, or tap 'Talk to a real person' below.");
        }
      }
      setMsgs((cur) => [...cur, { role: "assistant", content: reply }]);
      track("concierge_message", { intent: res.intent || "" });
      if (res.intent === "handoff") {
        setShowHandoff(true);
      }
    } catch (err) {
      setMsgs((cur) => [
        ...cur,
        {
          role: "assistant",
          content:
            "Sorry — I'm having trouble reaching the team. Tap 'Talk to a real person' below and we'll follow up.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const clearChat = () => {
    try {
      sessionStorage.removeItem(STORE_KEY);
      sessionStorage.removeItem(SESSION_KEY);
    } catch (e) { /* noop */ }
    setState(loadSession());
  };

  if (!open) {
    return (
      <>
        {showNudge && (
          <div
            data-testid="concierge-nudge"
            className="fixed bottom-24 right-6 z-40 bg-white border border-[#e5e7eb] shadow-xl max-w-[280px] p-4 animate-fadeIn"
            role="status"
          >
            <button
              type="button"
              onClick={() => setShowNudge(false)}
              aria-label="Dismiss"
              className="absolute top-1.5 right-1.5 p-1 text-[#6B7280] hover:text-[#080808]"
              data-testid="concierge-nudge-dismiss"
            >
              <X size={12} />
            </button>
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles size={12} className="text-[#3A7A8C]" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#3A7A8C]">
                Before launch
              </span>
            </div>
            <p className="text-sm text-[#080808] leading-snug">
              We&apos;re previewing the new site before our 24 August launch. Want to be one of
              the first to try it?
            </p>
            <button
              type="button"
              onClick={() => {
                setShowNudge(false);
                openChat();
                track("concierge_nudge_accept");
                // Pre-fill the input with a friendly opener so the LLM jumps into pitch mode.
                setInput("Tell me about the Early Access Preview.");
              }}
              data-testid="concierge-nudge-cta"
              className="mt-3 text-xs uppercase tracking-[0.15em] font-bold text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5"
            >
              Tell me more →
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={openChat}
          data-testid="concierge-toggle"
          aria-label="Open Ashlan Clinic concierge chat"
          className="fixed bottom-6 right-6 z-50 bg-[#3A7A8C] hover:bg-[#2F6776] text-white shadow-lg rounded-full p-4 flex items-center gap-2 transition-transform hover:-translate-y-0.5"
        >
          <MessageCircle size={20} />
          <span className="hidden sm:inline text-sm font-bold uppercase tracking-[0.15em]">Ask the clinic</span>
        </button>
      </>
    );
  }

  return (
    <div
      data-testid="concierge-panel"
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] max-w-[440px] h-[min(560px,calc(100vh-2rem))] bg-white border border-[#e5e7eb] shadow-2xl flex flex-col"
    >
      {/* Header */}
      <div className="bg-[#080808] text-white px-5 py-4 flex items-center gap-3">
        <Sparkles size={16} className="text-[#9DD4D8]" />
        <div className="flex-1">
          <div className="text-sm font-bold uppercase tracking-[0.15em]">Ashlan Concierge</div>
          <div className="text-[10px] text-[#9DD4D8] uppercase tracking-[0.18em]">Not a medical diagnosis tool</div>
        </div>
        <button type="button" onClick={closeChat} aria-label="Close" data-testid="concierge-close" className="text-white/70 hover:text-white">
          <X size={18} />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} data-testid="concierge-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFA]">
        {msgs.map((m, i) => (
          <Bubble key={i} role={m.role} content={m.content} onClose={closeChat} />
        ))}
        {busy && (
          <div className="flex items-center gap-2 text-xs text-[#6B7280]" data-testid="concierge-typing">
            <Loader2 size={12} className="animate-spin" /> Concierge is typing…
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-[#e5e7eb] bg-white">
        <div className="flex items-end gap-2 p-3">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type a question…"
            data-testid="concierge-input"
            rows={2}
            className="flex-1 resize-none text-sm border border-[#e5e7eb] focus:border-[#3A7A8C] focus:outline-none px-3 py-2"
          />
          <button type="button" disabled={busy || !input.trim()} onClick={send} data-testid="concierge-send"
            className="bg-[#3A7A8C] hover:bg-[#2F6776] disabled:bg-[#9CA3AF] text-white p-3 inline-flex items-center justify-center">
            <Send size={14} />
          </button>
        </div>
        <div className="px-3 pb-2 flex items-center justify-between flex-wrap gap-2">
          <button type="button" onClick={() => setShowHandoff(true)} data-testid="concierge-request-human"
            className="text-xs uppercase tracking-[0.15em] font-bold text-[#3A7A8C] hover:underline inline-flex items-center gap-1.5">
            <Phone size={11} /> Talk to a real person
          </button>
          <div className="flex items-center gap-3">
            <button type="button" onClick={suppressAutopop} className="text-[10px] text-[#9CA3AF] hover:text-[#080808] underline-offset-2 hover:underline" data-testid="concierge-suppress-autopop">
              Don&apos;t auto-open
            </button>
            <button type="button" onClick={clearChat} className="text-xs text-[#6B7280] hover:text-[#080808]" data-testid="concierge-reset">
              Reset chat
            </button>
          </div>
        </div>
        {/* Compliance footer — always visible inside the panel. */}
        <div className="px-3 pb-3 pt-1 border-t border-[#F3F4F6]">
          <p className="flex items-start gap-1.5 text-[10px] leading-snug text-[#6B7280]" data-testid="concierge-compliance-footer">
            <ShieldCheck size={11} className="text-[#3A7A8C] shrink-0 mt-px" />
            <span>
              Ashlan&apos;s Concierge offers information only — it does not provide
              medical advice, diagnosis, or treatment. For medical concerns, please
              consult your physician.
            </span>
          </p>
        </div>
      </div>

      {showHandoff && (
        <HandoffForm sessionId={sid} onClose={() => setShowHandoff(false)} />
      )}
    </div>
  );
}

const MarkdownLink = ({ href = "", children, onClose, ...rest }) => {
  const isInternal = href.startsWith("/") && !href.startsWith("//");
  if (isInternal) {
    return (
      <Link
        to={href}
        onClick={() => {
          if (typeof onClose === "function") onClose();
          track("concierge_link_click", { href });
        }}
        className="text-[#3A7A8C] underline underline-offset-2 hover:text-[#2c5f6e]"
        data-testid={`concierge-link-${href.replace(/[^a-z0-9]+/gi, "-")}`}
      >
        {children}
      </Link>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-[#3A7A8C] underline underline-offset-2 hover:text-[#2c5f6e]"
      {...rest}
    >
      {children}
    </a>
  );
};

function Bubble({ role, content, onClose }) {
  const markdownComponents = useMemo(
    () => ({
      // eslint-disable-next-line react/no-unstable-nested-components -- ReactMarkdown's `components.a` API expects an inline renderer; useMemo keeps the identity stable across renders.
      a: (props) => <MarkdownLink {...props} onClose={onClose} />,
    }),
    [onClose],
  );
  const isUser = role === "user";
  return (
    <div data-testid={`bubble-${role}`} className={`flex gap-2 ${isUser ? "justify-end" : ""}`}>
      {!isUser && (
        <div className="w-7 h-7 bg-[#3A7A8C] text-white flex items-center justify-center shrink-0">
          <Sparkles size={12} />
        </div>
      )}
      <div className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "bg-[#3A7A8C] text-white"
            : "bg-white border border-[#e5e7eb] text-[#080808]"
        }`}>
        {isUser ? (
          <p className="whitespace-pre-wrap">{content}</p>
        ) : (
          <div className="prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-a:text-[#3A7A8C]">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {content}
            </ReactMarkdown>
          </div>
        )}
      </div>
      {isUser && (
        <div className="w-7 h-7 bg-[#080808] text-white flex items-center justify-center shrink-0">
          <User size={12} />
        </div>
      )}
    </div>
  );
}

function HandoffForm({ sessionId, onClose }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    request_followup: false,
    consent_email: false,
    consent_sms: false,
  });
  const [sending, setSending] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (form.consent_sms && !form.phone.trim()) {
      toast.error("Phone number required for SMS opt-in.");
      return;
    }
    setSending(true);
    try {
      await conciergeHandoff({ session_id: sessionId, ...form });
      track("concierge_handoff_submit", {
        followup: form.request_followup ? 1 : 0,
        opt_email: form.consent_email ? 1 : 0,
        opt_sms: form.consent_sms ? 1 : 0,
      });
      const thanks = form.request_followup
        ? "Thanks — we've logged your request and one of our team will follow up by your preferred channel."
        : "Thanks — your message is with our team.";
      toast.success(thanks);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try the contact page.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="concierge-handoff" className="absolute inset-0 bg-white border border-[#3A7A8C] p-5 flex flex-col overflow-y-auto">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-bold text-[#080808] text-lg">Send us a message</h3>
          <p className="text-xs text-[#6B7280] mt-1">Our team only reaches out when you ask us to — your privacy is yours to control.</p>
        </div>
        <button type="button" onClick={onClose} className="text-[#6B7280] hover:text-[#080808]" aria-label="Cancel" data-testid="handoff-cancel">
          <X size={18} />
        </button>
      </div>
      <div className="flex flex-col gap-3 flex-1">
        <input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ash-input" data-testid="handoff-name" />
        <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ash-input" data-testid="handoff-email" />
        <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ash-input" data-testid="handoff-phone" />
        <textarea placeholder="Anything you'd like to add?" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="ash-input ash-textarea !min-h-[80px]" data-testid="handoff-message" />

        {/* Consent block — visitor must explicitly opt in to each channel. */}
        <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-3 space-y-2.5">
          <label className="flex items-start gap-2 cursor-pointer text-xs text-[#080808] leading-snug">
            <input
              type="checkbox"
              checked={form.request_followup}
              onChange={(e) => setForm({ ...form, request_followup: e.target.checked })}
              className="mt-0.5 accent-[#3A7A8C]"
              data-testid="handoff-followup"
            />
            <span>
              <span className="font-semibold">Yes — please have someone follow up with me.</span>
              <span className="block text-[#6B7280] mt-0.5">Without this, your message reaches us but no one will contact you proactively.</span>
            </span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-xs text-[#080808] leading-snug">
            <input
              type="checkbox"
              checked={form.consent_email}
              onChange={(e) => setForm({ ...form, consent_email: e.target.checked })}
              className="mt-0.5 accent-[#3A7A8C]"
              data-testid="handoff-opt-email"
            />
            <span>Send me occasional wellness tips and updates by email <span className="text-[#9CA3AF]">(unsubscribe anytime)</span>.</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer text-xs text-[#080808] leading-snug">
            <input
              type="checkbox"
              checked={form.consent_sms}
              onChange={(e) => setForm({ ...form, consent_sms: e.target.checked })}
              className="mt-0.5 accent-[#3A7A8C]"
              data-testid="handoff-opt-sms"
            />
            <span>Send me occasional text reminders <span className="text-[#9CA3AF]">(SMS, max 2/month, opt-out anytime)</span>.</span>
          </label>
        </div>
      </div>
      <button type="button" disabled={sending} onClick={submit} data-testid="handoff-submit" className="ash-btn-primary w-full justify-center mt-3">
        {sending ? <Loader2 size={14} className="animate-spin" /> : "Send to the team"}
      </button>
    </div>
  );
}
